import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@components/Button';
import DnsRecordCard from '@components/DnsRecordCard';
import DomainCheckStatus from '@components/DomainCheckStatus';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { channelsActions } from '@actions';
import { channelsService } from '@services';
import { handleApiError } from '@helpers/api';
import { EMAIL_DOMAIN_STATUS, STATUS } from '@constants/channels';

const Content = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
});

const STATUS_LABELS_KEYS = {
    [EMAIL_DOMAIN_STATUS.PENDING]: 'MicrositesPage.statusPending',
    [EMAIL_DOMAIN_STATUS.VERIFIED]: 'SettingsChannels.recordVerified',
    [EMAIL_DOMAIN_STATUS.FAILED]: 'MicrositesPage.statusFailed',
    [EMAIL_DOMAIN_STATUS.SUSPENDED]: 'SettingsChannels.statusSuspended',
};

const EmailDomainRecords = ({ domain, channelId, onDomainUpdated, collapseWhenVerified = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const channelStatus = useSelector(state => state.channels.data.find(channel => channel.id === channelId)?.status);
    const [checking, setChecking] = useState(false);
    const [recordsShown, setRecordsShown] = useState(false);

    if (!domain) return null;

    const suspended = domain.status === EMAIL_DOMAIN_STATUS.SUSPENDED;
    const needsCheck =
        !suspended &&
        (domain.status !== EMAIL_DOMAIN_STATUS.VERIFIED ||
            channelStatus !== STATUS.SUCCESS ||
            domain.records.some(record => record.status !== EMAIL_DOMAIN_STATUS.VERIFIED));
    const collapsed = collapseWhenVerified && !suspended && !needsCheck;
    const checkErrorKey =
        domain.status === EMAIL_DOMAIN_STATUS.FAILED
            ? 'apiErrors.email_domain_taken'
            : 'apiErrors.email_domain_check_failed';

    const statusLabel = status =>
        status == null
            ? t('DnsRecordCard.notChecked')
            : t(STATUS_LABELS_KEYS[status] || STATUS_LABELS_KEYS[EMAIL_DOMAIN_STATUS.PENDING]);

    const handleCheckStatus = async () => {
        setChecking(true);
        try {
            const updated = await channelsService.checkEmailDomain(domain.id);
            onDomainUpdated(updated);
            if (updated.status !== domain.status || channelStatus !== STATUS.SUCCESS) {
                dispatch(channelsActions.get());
            }
        } catch (err) {
            handleApiError(err, 'MicrositesPage.checkStatusError');
        } finally {
            setChecking(false);
        }
    };

    return (
        <Content>
            <DomainCheckStatus
                status={domain.status}
                statusLabel={statusLabel(domain.status)}
                lastCheckedAt={domain.lastCheckedAt}
                error={needsCheck && domain.lastCheckError ? t(checkErrorKey) : null}
                checking={checking}
                onCheck={needsCheck ? handleCheckStatus : null}
            />

            {suspended ? (
                <Typography variant="body2" sx={{ color: 'var(--error-main)' }}>
                    {t('SettingsChannels.domainSuspended')}
                </Typography>
            ) : null}

            {collapsed ? (
                <Button
                    variant="text"
                    onClick={() => setRecordsShown(shown => !shown)}
                    endIcon={recordsShown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    aria-expanded={recordsShown}
                    sx={{ alignSelf: 'flex-start', marginLeft: '-12px' }}
                >
                    {t(recordsShown ? 'SettingsChannels.hideDnsRecords' : 'SettingsChannels.showDnsRecords')}
                </Button>
            ) : (
                <>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                        {t('SettingsChannels.dnsRecordsHint')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('SettingsChannels.dnsPropagationNote')}
                    </Typography>
                </>
            )}

            {collapsed && !recordsShown ? null : (
                <div>
                    {domain.records.map(record => (
                        <DnsRecordCard
                            key={`${record.type}-${record.name}-${record.value}`}
                            type={record.type}
                            name={record.name}
                            value={record.value}
                            status={record.status}
                            statusLabel={statusLabel(record.status)}
                        />
                    ))}
                </div>
            )}
        </Content>
    );
};

export default EmailDomainRecords;
